import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "../modeSelect/modeSelect.css";
import "../quizEditor/quizEditor.css";
import "./pindropEditor.css";
import { selectAdminToken } from "../../sessionSlice";
import { selectSession, fetchSession } from "../../sessionBuilderSlice";

function blankItem() {
  return { prompt: "", image_url: null, polygons: [] };
}

const MAX_ITEMS = 10;
const MAX_PROMPT_CHARS = 150;
const IMG_W = 600;
const IMG_H = 400;

// How close (normalized units) a click must be to the first vertex to close
// the polygon being traced.
const CLOSE_THRESHOLD = 0.025;

// Standard point-in-polygon (ray casting), used to decide whether a click
// landed inside an existing shape (to select/move it) vs. empty canvas.
function pointInPolygon(px, py, points) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    const intersects =
      (yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function polygonToPath(points, W, H) {
  return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x * W} ${p.y * H}`).join(" ") + " Z";
}

const PindropEditor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activityId } = useParams();
  const adminToken = useSelector(selectAdminToken);
  const session = useSelector(selectSession);
  const currentTheme = useSelector(
    (s) => s.theme?.currentTheme || s.theme?.data?.themename || null
  );
  const API = process.env.REACT_APP_BACKEND_URL;

  const [items, setItems] = useState([blankItem()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);

  // Per-item interaction state, keyed by item index.
  const [tracing, setTracing] = useState({}); // { [idx]: { points: [{x,y}], cursor, hoverClose } }
  const [selectedPoly, setSelectedPoly] = useState({}); // { [idx]: polyIndex | null }
  const [dragState, setDragState] = useState({}); // { [idx]: { mode: 'move'|'vertex', polyIndex, vertexIndex, startX, startY, base } }
  const stageRefs = useRef({});

  useEffect(() => {
    dispatch(fetchSession());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/pp2/getActivityContent`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ activity_id: activityId, currentTheme }),
        });
        const json = await res.json();
        if (json.success && Array.isArray(json.data.content) && json.data.content.length) {
          setItems(
            json.data.content.map((it) => ({
              prompt: it.prompt || "",
              image_url: it.image_url || null,
              polygons: Array.isArray(it.polygons) ? it.polygons : [],
            }))
          );
        }
      } catch (e) {
        Swal.fire("Error", "Failed to load pin drop", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [activityId, adminToken, API, currentTheme]);

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const addItem = () => setItems((prev) => (prev.length >= MAX_ITEMS ? prev : [...prev, blankItem()]));
  const removeItem = (idx) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const handleDeleteAllItems = async () => {
    const confirm = await Swal.fire({
      title: "Delete all items?",
      text: `This removes all ${items.length} pin drop item(s) in this activity. This cannot be undone once saved.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d23b3b",
    });
    if (!confirm.isConfirmed) return;
    setItems([blankItem()]);
  };

  const checkImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.naturalWidth === IMG_W && img.naturalHeight === IMG_H) resolve();
        else
          reject(
            new Error(
              `Image must be exactly ${IMG_W}x${IMG_H}px. This file is ${img.naturalWidth}x${img.naturalHeight}px.`
            )
          );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read image."));
      };
      img.src = url;
    });

  const handleUpload = async (idx, file) => {
    if (!file) return;
    try {
      await checkImageDimensions(file);
    } catch (e) {
      Swal.fire("Wrong image size", e.message, "warning");
      return;
    }
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("currentTheme", currentTheme || "");
      const res = await fetch(`${API}/pp2/uploadPindropImage`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Upload failed");
      updateItem(idx, { image_url: json.image_url });
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
    } finally {
      setUploadingIdx(null);
    }
  };

  const toNorm = (idx, e) => {
    const rect = stageRefs.current[idx].getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  };

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // Replace one polygon in an item's list (live preview while dragging).
  const setPolygonLive = (idx, polyIndex, newPoints) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              polygons: it.polygons.map((p, pi) => (pi === polyIndex ? { points: newPoints } : p)),
            }
          : it
      )
    );
  };

  // --- Click handling: start/continue a trace, close it, or select existing shape ---
  const onStageClick = (idx, e) => {
    if (!items[idx].image_url || dragState[idx]) return;
    const p = toNorm(idx, e);
    const active = tracing[idx];

    // Already tracing a new polygon: add a vertex, or close if near the start.
    if (active && active.points.length > 0) {
      if (active.points.length >= 3 && dist(p, active.points[0]) <= CLOSE_THRESHOLD) {
        const polygons = [...items[idx].polygons, { points: active.points }];
        updateItem(idx, { polygons });
        setSelectedPoly((s) => ({ ...s, [idx]: polygons.length - 1 }));
        setTracing((t) => ({ ...t, [idx]: null }));
        return;
      }
      setTracing((t) => ({ ...t, [idx]: { points: [...active.points, p] } }));
      return;
    }

    // Not tracing: did the click land inside an existing polygon? Select it.
    const polys = items[idx].polygons;
    for (let pi = polys.length - 1; pi >= 0; pi -= 1) {
      if (pointInPolygon(p.x, p.y, polys[pi].points)) {
        setSelectedPoly((s) => ({ ...s, [idx]: pi }));
        return;
      }
    }

    // Empty space, not tracing -> start a brand new trace with this first vertex.
    setSelectedPoly((s) => ({ ...s, [idx]: null }));
    setTracing((t) => ({ ...t, [idx]: { points: [p] } }));
  };

  const onStageDoubleClick = (idx) => {
    const active = tracing[idx];
    if (active && active.points.length >= 3) {
      const polygons = [...items[idx].polygons, { points: active.points }];
      updateItem(idx, { polygons });
      setSelectedPoly((s) => ({ ...s, [idx]: polygons.length - 1 }));
      setTracing((t) => ({ ...t, [idx]: null }));
    }
  };

  const onStageMouseMove = (idx, e) => {
    const p = toNorm(idx, e);

    const drag = dragState[idx];
    if (drag) {
      const dx = p.x - drag.startX;
      const dy = p.y - drag.startY;
      if (drag.mode === "vertex") {
        const pts = drag.base.map((pt, i) =>
          i === drag.vertexIndex
            ? { x: Math.max(0, Math.min(1, pt.x + dx)), y: Math.max(0, Math.min(1, pt.y + dy)) }
            : pt
        );
        setPolygonLive(idx, drag.polyIndex, pts);
      } else if (drag.mode === "move") {
        const pts = drag.base.map((pt) => ({
          x: Math.max(0, Math.min(1, pt.x + dx)),
          y: Math.max(0, Math.min(1, pt.y + dy)),
        }));
        setPolygonLive(idx, drag.polyIndex, pts);
      }
      return;
    }

    const active = tracing[idx];
    if (active && active.points.length > 0) {
      const nearStart = active.points.length >= 3 && dist(p, active.points[0]) <= CLOSE_THRESHOLD;
      setTracing((t) => ({ ...t, [idx]: { ...active, cursor: p, hoverClose: nearStart } }));
    }
  };

  const onStageMouseUp = (idx) => {
    if (dragState[idx]) setDragState((prev) => ({ ...prev, [idx]: null }));
  };

  // Clicking a polygon's body begins a move-drag (also selects it).
  const onPolyMouseDown = (idx, polyIndex, e) => {
    if (tracing[idx]) return; // don't interfere with active tracing
    e.stopPropagation();
    const p = toNorm(idx, e);
    setSelectedPoly((s) => ({ ...s, [idx]: polyIndex }));
    setDragState((prev) => ({
      ...prev,
      [idx]: { mode: "move", polyIndex, startX: p.x, startY: p.y, base: items[idx].polygons[polyIndex].points },
    }));
  };

  // Dragging a vertex handle reshapes that single corner.
  const onVertexMouseDown = (idx, polyIndex, vertexIndex, e) => {
    e.stopPropagation();
    const p = toNorm(idx, e);
    setDragState((prev) => ({
      ...prev,
      [idx]: { mode: "vertex", polyIndex, vertexIndex, startX: p.x, startY: p.y, base: items[idx].polygons[polyIndex].points },
    }));
  };

  const removePolygon = (idx, pi) => {
    updateItem(idx, { polygons: items[idx].polygons.filter((_, k) => k !== pi) });
    setSelectedPoly((s) => ({ ...s, [idx]: null }));
  };

  const cancelTrace = (idx) => setTracing((t) => ({ ...t, [idx]: null }));

  const handleSave = async () => {
    for (let i = 0; i < items.length; i += 1) {
      if (!items[i].prompt.trim()) {
        Swal.fire("", `Item ${i + 1}: prompt is required`, "warning");
        return;
      }
      if (items[i].prompt.length > MAX_PROMPT_CHARS) {
        Swal.fire("", `Item ${i + 1}: prompt must be ${MAX_PROMPT_CHARS} characters or fewer`, "warning");
        return;
      }
      if (!items[i].image_url) {
        Swal.fire("", `Item ${i + 1}: upload an image`, "warning");
        return;
      }
      if (items[i].polygons.length === 0) {
        Swal.fire("", `Item ${i + 1}: draw at least one drop zone`, "warning");
        return;
      }
    }
    if (items.length > MAX_ITEMS) {
      Swal.fire("", `At most ${MAX_ITEMS} items allowed per pin drop activity`, "warning");
      return;
    }
    setSaving(true);
    try {
      const trimmedItems = items.map((it) => ({ ...it, prompt: it.prompt.trim() }));
      const res = await fetch(`${API}/pp2/savePindrop`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ activity_id: activityId, items: trimmedItems, currentTheme }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Save failed");
      Swal.fire("Saved", `${json.count} pin drop item(s) saved`, "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="load-theme-container">
        <div className="load-theme-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const STAGE_W = 1000;
  const STAGE_H = 1000; // virtual SVG viewBox units; scales via CSS to actual image size

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/session/activities")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="rules-page-new">
        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">Pin Drop Editor</div>

          <div className="qe-toolbar">
            <button className="qe-tool-btn" onClick={addItem} disabled={items.length >= MAX_ITEMS}>
              <i className="fa-solid fa-plus"></i> Add Pin Drop Item
            </button>
            {items.length > 1 && (
              <button className="qe-tool-btn qe-delete-all" onClick={handleDeleteAllItems}>
                <i className="fa-solid fa-trash"></i> Delete All
              </button>
            )}
          </div>

          <div className="qe-count code">
            {items.length} / {MAX_ITEMS} items
          </div>

          {items.map((item, idx) => {
            const active = tracing[idx];
            return (
              <div className="admin-tab-container qe-card" key={idx}>
                <div className="qe-card-head">
                  <span className="qe-qnum">Item {idx + 1}</span>
                  <span className="pd-rect-count">{item.polygons.length} zone(s)</span>
                  {items.length > 1 && (
                    <button className="qe-remove-q" onClick={() => removeItem(idx)}>
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  )}
                </div>

                <div className="form-group-v1">
                  <label>Prompt</label>
                  <input
                    type="text"
                    value={item.prompt}
                    maxLength={MAX_PROMPT_CHARS}
                    onChange={(e) => updateItem(idx, { prompt: e.target.value })}
                    placeholder="e.g., Drop a pin where you joined the company"
                  />
                  <div className="qe-hint code">Max {MAX_PROMPT_CHARS} characters</div>
                </div>

                <div className="pd-upload-row">
                  <label className="qe-media-upload">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      style={{ display: "none" }}
                      onChange={(e) => handleUpload(idx, e.target.files?.[0])}
                    />
                    <span className="ss-upload-btn">
                      {uploadingIdx === idx
                        ? "Uploading..."
                        : item.image_url
                        ? "Replace Image"
                        : "Upload Image"}
                    </span>
                  </label>
                  <span className="code">PNG / JPG / WEBP, exactly {IMG_W}×{IMG_H}px.</span>
                </div>

                {!item.image_url ? (
                  <div className="pd-no-image">Upload an image to draw drop zones.</div>
                ) : (
                  <>
                    <div className="code" style={{ margin: "8px 0" }}>
                      Click to place points and <strong>trace an outline</strong> around the
                      object — click back near your first point (or double-click) to close the
                      shape. Click inside a finished zone to select it: drag its body to move it,
                      or drag a corner dot to reshape it.
                      {active && (
                        <button className="pd-cancel-trace" onClick={() => cancelTrace(idx)}>
                          Cancel trace
                        </button>
                      )}
                    </div>
                    <div
                      className="pd-stage"
                      style={{ "--pd-ratio": `${IMG_W} / ${IMG_H}` }}
                      ref={(el) => {
                        stageRefs.current[idx] = el;
                      }}
                      onClick={(e) => onStageClick(idx, e)}
                      onDoubleClick={() => onStageDoubleClick(idx)}
                      onMouseMove={(e) => onStageMouseMove(idx, e)}
                      onMouseUp={() => onStageMouseUp(idx)}
                      onMouseLeave={() => onStageMouseUp(idx)}
                    >
                      <img
                        src={process.env.REACT_APP_S3_PATH + item.image_url}
                        alt="pindrop base"
                        draggable={false}
                      />
                      <svg className="pd-svg" viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} preserveAspectRatio="none">
                        {item.polygons.map((poly, pi) => {
                          const isSelected = selectedPoly[idx] === pi;
                          return (
                            <g key={pi}>
                              <path
                                d={polygonToPath(poly.points, STAGE_W, STAGE_H)}
                                className={`pd-poly ${isSelected ? "pd-poly-selected" : ""}`}
                                onMouseDown={(e) => onPolyMouseDown(idx, pi, e)}
                              />
                              {isSelected &&
                                poly.points.map((pt, vi) => (
                                  <circle
                                    key={vi}
                                    cx={pt.x * STAGE_W}
                                    cy={pt.y * STAGE_H}
                                    r={9}
                                    className="pd-vertex"
                                    onMouseDown={(e) => onVertexMouseDown(idx, pi, vi, e)}
                                  />
                                ))}
                            </g>
                          );
                        })}

                        {active && active.points.length > 0 && (
                          <g>
                            <polyline
                              points={
                                active.points.map((p) => `${p.x * STAGE_W},${p.y * STAGE_H}`).join(" ") +
                                (active.cursor ? ` ${active.cursor.x * STAGE_W},${active.cursor.y * STAGE_H}` : "")
                              }
                              className="pd-trace-line"
                            />
                            {active.points.map((p, i) => (
                              <circle
                                key={i}
                                cx={p.x * STAGE_W}
                                cy={p.y * STAGE_H}
                                r={i === 0 && active.hoverClose ? 13 : 7}
                                className={`pd-trace-dot ${i === 0 && active.hoverClose ? "pd-trace-dot-close" : ""}`}
                              />
                            ))}
                          </g>
                        )}
                      </svg>

                      {item.polygons.map((poly, pi) =>
                        selectedPoly[idx] === pi ? (
                          <button
                            key={`del-${pi}`}
                            className="pd-poly-del"
                            style={{ left: `${poly.points[0].x * 100}%`, top: `${poly.points[0].y * 100}%` }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              removePolygon(idx, pi);
                            }}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        ) : null
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <div className="code">
            Pins allowed per participant: <strong>{session?.pindrop_limit ?? 1}</strong> (set in
            Settings). Any drawn zone counts as correct — points are the same as a quiz correct
            answer, and stack if a participant drops more than one correct pin.
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Pin Drop"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PindropEditor;
